import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import type {
  PriceTableHistoryAction,
  PriceTableHistoryChange,
  PriceTableHistoryEntry,
} from '@/api/commercial/price-tables';
import { priceTableApi } from '@/api/commercial/price-tables';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const ACTION_LABELS: Record<PriceTableHistoryAction, string> = {
  CREATED: 'Criado',
  UPDATED: 'Atualizado',
  PUBLISHED: 'Programada publicação',
  ACTIVATED: 'Ativado',
  PAUSED: 'Pausado',
  RESUMED: 'Retomado',
  CANCELLED: 'Cancelado',
  INACTIVATED: 'Inativado',
  CLONED: 'Clonado',
};

const ITEMS_PER_PAGE = 10;

const FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  startDateTime: 'Data de início',
  endDateTime: 'Data de fim',
  isTemporary: 'Temporária',
  status: 'Status',
  groups: 'Condições de faturamento',
  equipmentPayment: 'Equipamentos (sem SIM)',
  equipmentSimcardPayment: 'Equipamentos (com SIM)',
  simcardPayment: 'SIM Cards',
  servicePayment: 'Serviços',
};

const COMPLEX_FIELDS = new Set([
  'equipmentPayment',
  'equipmentSimcardPayment',
  'simcardPayment',
  'servicePayment',
  'groups',
]);

const COMPLEX_FIELD_MESSAGES: Record<string, string> = {
  equipmentPayment: 'Alterações nos equipamentos (sem SIM)',
  equipmentSimcardPayment: 'Alterações nos equipamentos (com SIM)',
  simcardPayment: 'Alterações no SIM Card',
  servicePayment: 'Alterações nos serviços',
  groups: 'Alterações nas condições de faturamento',
};

function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const dateMatch = /^\d{4}-\d{2}-\d{2}T/.test(value);
    if (dateMatch) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return format(date, 'dd/MM/yyyy HH:mm');
      }
    }
    return value;
  }
  return JSON.stringify(value);
}

function ChangeDetails({ changes }: { changes: PriceTableHistoryChange[] }) {
  return (
    <div className="space-y-1">
      {changes.map((change) => {
        if (COMPLEX_FIELDS.has(change.field)) {
          return (
            <div key={change.field} className="text-xs">
              <span className="text-gray-600">{COMPLEX_FIELD_MESSAGES[change.field]}</span>
            </div>
          );
        }

        const label = FIELD_LABELS[change.field] ?? change.field;

        return (
          <div key={change.field} className="text-xs">
            <span className="font-medium">{label}:</span>{' '}
            {change.from === null || change.from === undefined ? (
              <span className="text-gray-600">{formatChangeValue(change.to)}</span>
            ) : (
              <>
                <span className="text-red-600">{formatChangeValue(change.from)}</span>
                {' → '}
                <span className="text-green-600">{formatChangeValue(change.to)}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HistoryEntryDetails({ entry }: { entry: PriceTableHistoryEntry }) {
  if (entry.action === 'UPDATED' && entry.changes && entry.changes.length > 0) {
    return <ChangeDetails changes={entry.changes} />;
  }

  return <span className="text-xs text-gray-500">{ACTION_LABELS[entry.action]}</span>;
}

interface PriceTableHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceTableId: string;
  priceTableName: string;
}

export function PriceTableHistoryDialog({
  open,
  onOpenChange,
  priceTableId,
  priceTableName,
}: PriceTableHistoryDialogProps) {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['price-table-history', priceTableId, page],
    queryFn: () => priceTableApi.getHistory(priceTableId, { page, limit: ITEMS_PER_PAGE }),
    enabled: open && !!priceTableId,
  });

  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Histórico de alterações - {priceTableName}</DialogTitle>
          <DialogDescription>
            Registro de todas as alterações realizadas nesta tabela
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : data && data.data.length > 0 ? (
          <div className="space-y-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {format(new Date(entry.created_at), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                      <TableCell className="text-sm">{entry.userName}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                          {ACTION_LABELS[entry.action]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <HistoryEntryDetails entry={entry} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={data.total}
                limit={data.limit}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-gray-500">
            Nenhum registro de histórico encontrado
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
